import { FC, useMemo } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import type { MarginWaterfallTotals, WaterfallChartRow } from '../margin.types';
import { FORECASTING_THEME, STAGE_COLORS } from '../forecastingTheme';
import { formatCurrency, formatPercent } from '../utils/forecastingAggregations';
import { buildWaterfallChartData } from '../utils/marginAggregations';
import { ChartPlot, ChartTitle, Legend, Panel } from '../Forecasting.styles';
import { currencyAxisProps } from '../utils/currencyAxis';
import { useShellPalette } from '@/styles';

interface MarginWaterfallChartProps {
  totals: MarginWaterfallTotals;
}

function stepColor(kind: WaterfallChartRow['kind'], negative: string): string {
  if (kind === 'start') return STAGE_COLORS.Booked;
  if (kind === 'end') return STAGE_COLORS.Pipe;
  return negative;
}


function WaterfallTooltip({
  active,
  payload
}: {
  active?: boolean;
  payload?: { payload: WaterfallChartRow }[];
}) {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  if (row.key === 'marginPct') {
    return (
      <div
        style={{
          background: '#fff',
          color: '#333',
          padding: '10px 14px',
          borderRadius: 4,
          fontSize: 13,
          boxShadow: '0 2px 8px rgba(0,0,0,0.18)'
        }}
      >
        <strong>{row.label}</strong>
        <div>{formatPercent(row.displayValue, 1)}</div>
      </div>
    );
  }

  return (
    <div
      style={{
        background: '#fff',
        color: '#333',
        padding: '10px 14px',
        borderRadius: 4,
        fontSize: 13,
        boxShadow: '0 2px 8px rgba(0,0,0,0.18)'
      }}
    >
      <strong>{row.label}</strong>
      <div>{formatCurrency(row.displayValue)}</div>
      {row.kind === 'decrease' && (
        <div style={{ fontSize: 11, color: '#666', marginTop: 4 }}>Deduction from revenue</div>
      )}
    </div>
  );
}

export const MarginWaterfallChart: FC<MarginWaterfallChartProps> = ({ totals }) => {
  /* Resolved colours, not CSS variables: Recharts writes these into SVG
     attributes, where var() is not reliably supported. */
  const shell = useShellPalette();
  const chartRows = useMemo(() => buildWaterfallChartData(totals), [totals]);
  const barRows = chartRows.filter((row) => row.key !== 'marginPct');
  const maxY = Math.max(totals.revenue, totals.grossProfit, 1);

  return (
    <Panel>
      <ChartTitle>Margin waterfall</ChartTitle>
      <Legend>
        <span>
          <i style={{ background: STAGE_COLORS.Booked }} />
          Revenue / start
        </span>
        <span>
          <i style={{ background: FORECASTING_THEME.negative }} />
          Cost / deduction
        </span>
        <span>
          <i style={{ background: STAGE_COLORS.Pipe }} />
          Gross profit
        </span>
      </Legend>
      <ChartPlot>
        <ResponsiveContainer width="100%" height={360}>
          <BarChart data={barRows} margin={{ top: 28, right: 16, left: 4, bottom: 8 }} accessibilityLayer={false}>
            <CartesianGrid stroke={shell.chartGrid} vertical={false} />
            <XAxis
              dataKey="label"
              tick={{ fill: shell.dataText, fontSize: 10, fontWeight: 400 }}
              axisLine={{ stroke: shell.chartAxisLine }}
              tickLine={false}
              interval={0}
            />
            <YAxis
              tick={{ fill: shell.dataText, fontSize: 11, fontWeight: 400 }}
              axisLine={false}
              tickLine={false}
              {...currencyAxisProps(maxY)}
              width={52}
            />
            <Tooltip content={<WaterfallTooltip />} cursor={{ fill: shell.menuHoverBg }} />
            <Bar dataKey="helper" stackId="waterfall" fill="transparent" isAnimationActive={false} />
            <Bar dataKey="amount" stackId="waterfall" radius={[3, 3, 0, 0]} isAnimationActive={false}>
              {barRows.map((row) => (
                <Cell key={row.key} fill={stepColor(row.kind, shell.negative)} />
              ))}
              <LabelList
                dataKey="displayValue"
                position="top"
                formatter={(value: number) => formatCurrency(value)}
                style={{ fill: shell.dataText, fontSize: 10, fontWeight: 400 }}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </ChartPlot>
    </Panel>
  );
};
